// Fresh-eyes 12 (Carla, 2026-10-12): the Dashboard's Team overview tile.
//
// It said "No team task data yet" while /team showed 82 open tasks for the same person: it counted
// the caller's OWN task list (the Dashboard's `tasks`) for everyone else on the team. It now reads
// the team route, the same endpoint and scope /team uses (GET /api/tasks?team=true, one row asked
// for, since only the numbers are shown), and shows the server's numbers (`summary`, made once in
// Aidey Backend/lib/teamTaskPage.js teamTaskSummary). Scope 'own' (a member who only sees their own
// tasks) is said, never called the team's. A failed load is said, with the server's reason.
import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';
import { Avatar, DashboardCard, ErrorState, HeaderAction, ProgressBar, Skeleton, StatTile, StatusPill } from '../../ui';

// The same request /team's lists make, one row a page: the summary is over every row the scope chose.
export const TEAM_SUMMARY_URL = '/api/tasks?team=true&page=1&pageSize=1';

export default function TeamOverviewTile({ isTeamAdmin = false, onOpenTeam }) {
  const [state, setState] = useState({ loading: true, error: '', summary: null, scope: 'own', members: [] });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: '' }));
    try {
      const [membersRes, tasksRes] = await Promise.all([api('/api/team/members'), api(TEAM_SUMMARY_URL)]);
      const membersData = await membersRes.json();
      const data = await tasksRes.json();
      if (!data || !data.summary) throw new Error('The team\'s numbers did not come back. Try again.');
      setState({
        loading: false, error: '', summary: data.summary,
        scope: data.scope === 'team' ? 'team' : 'own',
        members: (membersData?.members || []).filter((m) => m.status !== 'inactive'),
      });
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: messageFromError(err, 'Your team\'s tasks could not be loaded.') }));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const { loading, error, summary, scope, members } = state;
  const ownOnly = scope !== 'team';
  const people = ownOnly || !summary ? [] : members
    .map((user) => ({ user, ...(summary.byPerson?.[user.id] || { total: 0, completed: 0, outstanding: 0, overdue: 0 }) }))
    .filter((s) => s.total > 0);
  const topCompleter = [...people].sort((a, b) => b.completed - a.completed)[0];
  const mostOutstanding = [...people].sort((a, b) => b.outstanding - a.outstanding)[0];

  return (
    <DashboardCard tint="pastel-lilac" title="Team overview" titleAs="h3" className="team-overview-card"
      meta={isTeamAdmin && onOpenTeam ? <HeaderAction onClick={onOpenTeam}>Full dashboard →</HeaderAction> : null}>
      {loading && !summary ? (
        <Skeleton lines={3} label="Loading your team's tasks" />
      ) : error ? (
        <ErrorState title="Your team's tasks did not load" message={error} onRetry={load} />
      ) : (
        <>
          <div className="dash-team-stats">
            <StatTile label={ownOnly ? 'Your tasks' : 'Total tasks'} value={summary.total} />
            <StatTile label="Completed" value={summary.completed} />
            <StatTile label="Outstanding" value={summary.outstanding} />
            <StatTile label="Overdue" value={summary.overdue} />
          </div>

          {ownOnly && (
            <p className="dash-text">These are your own tasks. Only the owner and the finance role see the whole team&apos;s.</p>
          )}

          {people.length > 0 && (
            <div className="dash-members">
              {people.map(({ user, total, completed, outstanding, overdue }) => (
                <div key={user.id} className="dash-member">
                  <Avatar name={user.name} size={36} color="pastel-lilac" />
                  <ProgressBar label={user.name} value={completed} max={total} valueText={`${completed} of ${total} done`} />
                  <span className="dash-member__tags">
                    <StatusPill tone="pending">{outstanding} open</StatusPill>
                    {overdue > 0 && <StatusPill tone="warning">{overdue} overdue</StatusPill>}
                  </span>
                </div>
              ))}
            </div>
          )}

          {people.length > 0 && (
            <div className="dash-callouts">
              {topCompleter && <span>🏆 Most completed: <strong>{topCompleter.user.name}</strong> ({topCompleter.completed})</span>}
              {mostOutstanding && mostOutstanding.outstanding > 0 && (
                <span>📋 Most work: <strong>{mostOutstanding.user.name}</strong> ({mostOutstanding.outstanding} open)</span>
              )}
            </div>
          )}
        </>
      )}
    </DashboardCard>
  );
}
